import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "../../../../../Components/Utils/Breadcrumb";
import SearchBar from "../../../../../Components/Utils/SearchBar";
import {
  CreateButton,
  FilterButton,
  CancelButton,
  SubmitButton,
} from "../../../../../Components/Utils/Buttons";
import { useFormik } from "formik";
import FormikSelectTwo from "../../../../../Components/Utils/Select";
import UseGetDataSearch from "../../../../../Components/Hooks/useFetchDataBySearch";
import { useDeleteData } from "../../../../../Components/Hooks/useDeleteData";
import UseDropDown from "../../../../../Components/Hooks/useDropDown";
import ModalContainer from "../../../../../Components/Others/ModalContainer";
import useFetchMataData from "../../../../../Components/Hooks/useFetchMataData";
import NuDateDisplay from "../../../../../Components/Utils/NuDate";
import PremiumTable from "../../../../../Components/Others/PremiumTable";
import { GetAccess, GetRole } from "../../../../../Components/Utils/roles";

export default function MeetingMinutesListPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });

  const { value: categoryData } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "MeetingMinutes_Category"
  );

  const { value: statusData } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "MeetingMinutes_Status"
  );

  const { data: usersList } = UseDropDown("/user/drop-down");

  const categoryOptionsWithAll = useMemo(() => {
    const list = Array.isArray(categoryData) ? categoryData : [];
    return [{ value: "all", label: "All Categories" }, ...list];
  }, [categoryData]);

  const statusOptionsWithAll = useMemo(() => {
    const list = Array.isArray(statusData) ? statusData : [];
    return [{ value: "all", label: "All Status" }, ...list];
  }, [statusData]);

  const organizerOptionsWithAll = useMemo(() => {
    const list = Array.isArray(usersList) ? usersList : [];
    return [{ value: "all", label: "All Organizers" }, ...list];
  }, [usersList]);

  const [filterState, setFilterState] = useState({
    category: "all",
    status: "all",
    organizer_id: "all",
    from_date: "",
    to_date: "",
  });

  const filterFormik = useFormik({
    initialValues: filterState,
    enableReinitialize: true,
    onSubmit: (values) => {
      setFilterState(values);
      setPageIndex(1);
      setShowFilterDialog(false);
    },
  });

  const clearFilters = () => {
    filterFormik.resetForm({
      values: {
        category: "all",
        status: "all",
        organizer_id: "all",
        from_date: "",
        to_date: "",
      },
    });
    setFilterState({
      category: "all",
      status: "all",
      organizer_id: "all",
      from_date: "",
      to_date: "",
    });
    setShowFilterDialog(false);
    setPageIndex(1);
  };

  const isFiltered =
    (filterState.category && filterState.category !== "all") ||
    (filterState.status && filterState.status !== "all") ||
    (filterState.organizer_id && filterState.organizer_id !== "all") ||
    Boolean(filterState.from_date) ||
    Boolean(filterState.to_date);

  const queryParams = useMemo(() => {
    return {
      search: searchQuery || undefined,
      page: pageIndex,
      limit: itemsPerPage,
      status:
        filterState.status && filterState.status !== "all"
          ? filterState.status
          : undefined,
      category:
        filterState.category && filterState.category !== "all"
          ? filterState.category
          : undefined,
      organizer_id:
        filterState.organizer_id && filterState.organizer_id !== "all"
          ? filterState.organizer_id
          : undefined,
      from_date: filterState.from_date || undefined,
      to_date: filterState.to_date || undefined,
    };
  }, [searchQuery, pageIndex, itemsPerPage, filterState]);

  const { data, totalCount, getData, dataLoading } = UseGetDataSearch(
    "/meetings/points/",
    queryParams
  );

  const { handle: handleDelete } = useDeleteData(getData);

  const handleConfirmDelete = () => {
    if (!deleteModal.item) return;
    const targetId = deleteModal.item.id || deleteModal.item.meeting_id;
    handleDelete(`/meetings/points/${targetId}`, (val) => {
      setDeleteModal({ open: Boolean(val), item: null });
    });
  };

  const meetingsList = useMemo(() => {
    return Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : [];
  }, [data]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "Completed":
      case "Approved":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "Scheduled":
      case "Ongoing":
      case "In Progress":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "Draft":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "Cancelled":
        return "bg-rose-50 text-rose-700 border-rose-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  return (
    <div className="w-full h-full p-2 lg:px-4">
      {/* Top Header Bar with Breadcrumb, Search, Filter and Create Button */}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <Breadcrumb
          items={[
            { name: "Meeting" },
            { name: "Meeting Minutes" },
          ]}
        />
        <div className="flex flex-wrap items-center gap-2">
          <SearchBar
            value={searchQuery}
            handleChange={(e) => {
              setSearchQuery(e.target.value);
              setPageIndex(1);
            }}
            placeholder="Search meetings..."
          />
          <FilterButton
            handleClick={() => setShowFilterDialog(true)}
            label={isFiltered ? "Filtered" : "Filter"}
          />
          {isFiltered && (
            <FilterButton handleClick={clearFilters} label="Clear" />
          )}
          {(GetAccess("meeting_points_create") || GetRole()) && (
            <Link to="/meeting/meeting-points/create">
              <CreateButton label="Add Minutes" />
            </Link>
          )}
        </div>
      </div>

      <PremiumTable
        data={meetingsList}
        total={totalCount}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        dataCount={itemsPerPage}
        setDataCount={setItemsPerPage}
        dataLoading={dataLoading}
        headers={[
          { title: "S.No", width: "60px", align: "center" },
          { title: "TITLE", width: "auto" },
          { title: "CATEGORY", width: "140px" },
          { title: "DATE & TIME", width: "180px" },
          { title: "LOCATION", width: "160px" },
          { title: "ORGANIZER", width: "160px" },
          { title: "STATUS", width: "120px" },
          { title: "ACTIONS", align: "center", width: "120px" },
        ]}
        renderRow={(row, index) => {
          const targetId = row.id || row.meeting_id || index + 1;
          const dateDisplay = row.date
            ? NuDateDisplay({ value: row.date, format: "DD-MMM-YYYY" })
            : "-";
          const timeRange =
            row.start_time || row.startTime
              ? `${row.start_time || row.startTime} - ${
                  row.end_time || row.endTime || ""
                }`
              : "";

          const organizerName =
            row.organizer_name ||
            row.organizer?.name ||
            row.organizerName ||
            (row.organizer_id ? `User #${row.organizer_id}` : "-");

          return (
            <tr
              key={targetId}
              className="hover:bg-gray-50/50 transition-colors"
            >
              <td className="p-4 text-center align-middle border-t border-b border-l border-gray-200/60 rounded-tl-xl rounded-bl-xl text-xs font-semibold text-gray-500">
                {(pageIndex - 1) * itemsPerPage + index + 1}
              </td>
              <td className="p-4 align-middle border-t border-b border-gray-200/60 font-bold text-xs text-gray-900 max-w-xs truncate">
                {GetAccess("meeting_points_view") || GetRole() ? (
                  <Link
                    to={`/meeting/meeting-points/view/${targetId}`}
                    state={{
                      data: row,
                      pageIndex: pageIndex,
                      searchQuery: searchQuery,
                    }}
                    className="hover:text-[#048178] hover:underline"
                  >
                    {row.meeting_title || row.title || `Meeting #${targetId}`}
                  </Link>
                ) : (
                  <span>{row.meeting_title || row.title || `Meeting #${targetId}`}</span>
                )}
              </td>
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-xs font-semibold text-gray-600">
                {row.category || "-"}
              </td>
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-xs font-semibold text-gray-600 whitespace-nowrap">
                <div>{dateDisplay}</div>
                {timeRange && (
                  <div className="text-[10px] text-gray-400">
                    {timeRange}
                  </div>
                )}
              </td>
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-xs font-semibold text-gray-600 max-w-[140px] truncate">
                {row.location || "-"}
              </td>
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-xs font-semibold text-gray-600">
                {organizerName}
              </td>
              <td className="p-4 align-middle border-t border-b border-gray-200/60 whitespace-nowrap">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${getStatusBadge(
                    row.status
                  )}`}
                >
                  {row.status || "Scheduled"}
                </span>
              </td>
              <td className="p-4 text-center align-middle border-t border-b border-r border-gray-200/60 rounded-tr-xl rounded-br-xl">
                <div className="flex items-center justify-center gap-1.5">
                  {(GetAccess("meeting_points_view") || GetRole()) && (
                    <Link
                      to={`/meeting/meeting-points/view/${targetId}`}
                      state={{
                        data: row,
                        pageIndex: pageIndex,
                        searchQuery: searchQuery,
                      }}
                      className="border border-blue-200 text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                      title="View Minutes"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </Link>
                  )}

                  {(GetAccess("meeting_points_edit") || GetRole()) && (
                    <Link
                      to={`/meeting/meeting-points/edit/${targetId}`}
                      state={{
                        data: row,
                        from: "/meeting/meeting-points",
                        pageIndex: pageIndex,
                        searchQuery: searchQuery,
                      }}
                      className="border border-sky-200 text-[#0ea5e9] hover:bg-sky-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                      title="Edit Minutes"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z"
                        />
                      </svg>
                    </Link>
                  )}

                  {(GetAccess("meeting_points_delete") || GetRole()) && (
                    <button
                      type="button"
                      onClick={() =>
                        setDeleteModal({ open: true, item: row })
                      }
                      className="border border-red-200 text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center cursor-pointer"
                      title="Delete Minutes"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          );
        }}
      />

      {/* Filter Modal Dialog */}
      {showFilterDialog && (
        <div className="fixed top-0 left-0 w-full h-full bg-gray-800 bg-opacity-50 z-[9999] flex justify-center items-center p-3 sm:p-4">
          <div className="animate-scale-in w-full sm:w-[500px] max-w-[95vw] max-h-[90vh] bg-white py-4 px-4 sm:px-6 rounded-xl shadow-lg overflow-y-auto">
            <h2 className="text-base font-bold text-gray-800 mb-4">
              Filter Meeting Minutes
            </h2>
            <form onSubmit={filterFormik.handleSubmit}>
              <div className="flex flex-col gap-3 mb-4">
                <FormikSelectTwo
                  type="text"
                  label="Category"
                  placeholder="Choose Category"
                  Options={categoryOptionsWithAll}
                  formik={filterFormik}
                  name="category"
                  width="w-full"
                />

                <FormikSelectTwo
                  type="text"
                  label="Status"
                  placeholder="Choose Status"
                  Options={statusOptionsWithAll}
                  formik={filterFormik}
                  name="status"
                  width="w-full"
                />

                <FormikSelectTwo
                  type="text"
                  label="Organizer"
                  placeholder="Choose Organizer"
                  Options={organizerOptionsWithAll}
                  formik={filterFormik}
                  name="organizer_id"
                  width="w-full"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700 block mb-1">
                      From Date
                    </label>
                    <input
                      type="date"
                      className="text-xs border border-gray-300 rounded-md px-3 py-2 w-full outline-none focus:border-[#048178]"
                      value={filterFormik.values.from_date || ""}
                      onChange={(e) =>
                        filterFormik.setFieldValue("from_date", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 block mb-1">
                      To Date
                    </label>
                    <input
                      type="date"
                      className="text-xs border border-gray-300 rounded-md px-3 py-2 w-full outline-none focus:border-[#048178]"
                      value={filterFormik.values.to_date || ""}
                      onChange={(e) =>
                        filterFormik.setFieldValue("to_date", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <CancelButton handleClick={() => setShowFilterDialog(false)} />
                <SubmitButton name="Apply" />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Standard Delete Modal Dialog */}
      <ModalContainer
        visiable={deleteModal.open}
        title="Warning"
        content="Are You Sure! You want to Delete?"
        handleClick={handleConfirmDelete}
        closeModal={() => setDeleteModal({ open: false, item: null })}
      />
    </div>
  );
}
